import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { motion } from 'motion/react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import EditorialLoader from './components/EditorialLoader';
import { StorefrontDataProvider } from './context/StorefrontDataContext';
import SmoothScroll from './components/SmoothScroll';
import CartDrawer from './components/CartDrawer';
import VoucherPromoPopup from './components/VoucherPromoPopup';
import GlobalMasterDrawer from './components/live-editor/GlobalMasterDrawer';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { AdminAuthProvider } from './context/AdminAuthContext';
import { VoucherProvider } from './context/VoucherContext';
import ErrorPage from './pages/ErrorPage';
import { initAnalytics, trackPageView } from './utils/analytics';


const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const Shop = lazy(() => import('./pages/Shop'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Careers = lazy(() => import('./pages/Careers'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const About = lazy(() => import('./pages/About'));
const LegalPageLazy = lazy(() => import('./pages/LegalPage'));
const BlogList = lazy(() => import('./pages/blog/BlogList'));
const BlogDetail = lazy(() => import('./pages/blog/BlogDetail'));

// Lazy load storefront subpages
const AccountPage = lazy(() => import('./pages/AccountPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const OrderTrackingPage = lazy(() => import('./pages/OrderTrackingPage'));

// Lazy load admin pages & layouts
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminCustomers = lazy(() => import('./pages/admin/AdminCustomers'));
const AdminCMS = lazy(() => import('./pages/admin/AdminCMS'));
const AdminPagesHub = lazy(() => import('./pages/admin/AdminPagesHub'));
const AdminJobs = lazy(() => import('./pages/admin/AdminJobs'));
const AdminApplications = lazy(() => import('./pages/admin/AdminApplications'));
const AdminNotificationDeliveries = lazy(() => import('./pages/admin/AdminNotificationDeliveries'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminVouchers = lazy(() => import('./pages/admin/AdminVouchers'));
const AdminUserManagement = lazy(() => import('./pages/admin/AdminUserManagement'));
const AdminProfile = lazy(() => import('./pages/admin/AdminProfile'));
const AdminReviews = lazy(() => import('./pages/admin/AdminReviews'));
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'));
const AdminBlogEditor = lazy(() => import('./pages/admin/AdminBlogEditor'));
const AdminEmailTemplates = lazy(() => import('./pages/admin/AdminEmailTemplates'));
const AdminBulkPrintInvoices = lazy(() => import('./pages/admin/AdminBulkPrintInvoices'));
const AdminBulkPrintLabels = lazy(() => import('./pages/admin/AdminBulkPrintLabels'));
const AdminPopups = lazy(() => import('./pages/admin/AdminPopups'));


function RouteFallback() {
  return (
    <div className="flex flex-col items-center justify-center h-[50vh] w-full space-y-3 bg-[#FCFCFB]/40 select-none">
      <span className="text-label text-brand-wine/50 tracking-[0.25em] text-[9px] uppercase">
        Memuat Halaman
      </span>
      <div className="w-16 h-[1px] bg-ink/5 overflow-hidden relative">
        <motion.div
          initial={{ x: '-100%' }}
          animate={{ x: '100%' }}
          transition={{
            repeat: Infinity,
            duration: 1.2,
            ease: 'linear'
          }}
          className="absolute inset-0 bg-brand-wine w-8"
        />
      </div>
    </div>
  );
}

function AppContent() {
  const { pathname } = useLocation();
  const isAdminPage = pathname.startsWith('/admin');

  // Initialize GA4 and TikTok Pixel dynamically at application startup
  useEffect(() => {
    initAnalytics();
  }, []);

  // Track page navigation changes in the SPA
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname);
  }, [pathname]);

  return (
    <>
      {!isAdminPage && <EditorialLoader />}
      {!isAdminPage && <SmoothScroll />}
      {!isAdminPage && <CartDrawer />}
      {!isAdminPage && <VoucherPromoPopup />}
      {!isAdminPage && <GlobalMasterDrawer />}
      <div className="flex flex-col min-h-screen">
        {!isAdminPage && <Navbar />}
        <Suspense fallback={<RouteFallback />}>
          <main className="flex-grow">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/koleksi" element={<Shop />} />
              
              {/* Careers */}
              <Route path="/careers" element={<Careers />} />
              <Route path="/karir" element={<Careers />} />
              <Route path="/careers/:slug" element={<JobDetail />} />
              <Route path="/karir/:slug" element={<JobDetail />} />

              {/* Static Pages */}
              <Route path="/about" element={<About />} />
              <Route path="/tentang" element={<About />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/pembayaran/:orderId" element={<PaymentPage />} />
              <Route path="/akun" element={<AccountPage />} />
              <Route path="/account" element={<Navigate replace to="/akun" />} />
              <Route path="/my-orders" element={<Navigate replace to="/akun" />} />
              <Route path="/pesanan-saya" element={<Navigate replace to="/akun" />} />
              <Route path="/profile" element={<Navigate replace to="/akun?tab=profile" />} />

              {/* Blog */}
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogDetail />} />

              {/* Product Detail - strictly under /shop or /koleksi */}
              <Route path="/shop/:slug" element={<ProductDetail />} />
              <Route path="/koleksi/:slug" element={<ProductDetail />} />
              <Route path="/product/:slug" element={<ProductDetail />} />
              
              {/* Order Tracking Page — must be before /:slug catch-all */}
              <Route path="/pesanan/:orderId" element={<OrderTrackingPage />} />
              <Route path="/lacak/:orderId" element={<OrderTrackingPage />} />
              <Route path="/lacak" element={<Navigate replace to="/akun" />} />

              {/* Dynamic Legal & Static Pages (matches anything not caught above) */}
              <Route path="/:slug" element={<LegalPageLazy />} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="customers" element={<AdminCustomers />} />
                <Route path="vouchers" element={<AdminVouchers />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="cms" element={<AdminPagesHub />} />
                <Route path="cms/classic" element={<AdminCMS />} />
                <Route path="pages" element={<AdminPagesHub />} />
                <Route path="jobs" element={<AdminJobs />} />
                <Route path="applications" element={<AdminApplications />} />
                <Route path="notification-deliveries" element={<AdminNotificationDeliveries />} />
                <Route path="users" element={<AdminUserManagement />} />
                <Route path="profile" element={<AdminProfile />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="blog" element={<AdminBlog />} />
                <Route path="blog/new" element={<AdminBlogEditor />} />
                <Route path="blog/edit/:id" element={<AdminBlogEditor />} />
                <Route path="email-templates" element={<AdminEmailTemplates />} />
                <Route path="popups" element={<AdminPopups />} />
              </Route>

              {/* Bulk print routes — standalone pages without AdminLayout */}
              <Route path="/admin/print/invoices" element={<AdminBulkPrintInvoices />} />
              <Route path="/admin/print/labels" element={<AdminBulkPrintLabels />} />

              {/* Catch-all 404 */}
              <Route path="*" element={<ErrorPage mode="404" />} />
            </Routes>
          </main>
          {!isAdminPage && <Footer />}
        </Suspense>
      </div>
      <Toaster position="bottom-right" />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AdminAuthProvider>
        <VoucherProvider>
          <CartProvider>
            <Router>
              <StorefrontDataProvider>
                <AppContent />
              </StorefrontDataProvider>
            </Router>
          </CartProvider>
        </VoucherProvider>
      </AdminAuthProvider>
    </AuthProvider>
  );
}
